import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IDeleteAutoDebitConfigRequest {
  id: string;
  pin: string;
}

interface IDeleteAutoDebitConfigResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IDeleteAutoDebitConfigResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const deleteAutoDebitConfigAsync = createAsyncThunk(
  'savings/autoDebit/delete',
  async (
    { id, pin }: IDeleteAutoDebitConfigRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.delete<IDeleteAutoDebitConfigResponse>(
        `/savings/auto-debit-configurations/${id}`,
        {
          ...getSavingsAuthConfig(state),
          data: { pin },
        },
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Auto debit configuration removed',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IDeleteAutoDebitConfigResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const deleteAutoDebitConfigSlice = createSlice({
  name: 'savings/autoDebit/delete',
  initialState,
  reducers: {
    resetDeleteAutoDebitConfig(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteAutoDebitConfigAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(deleteAutoDebitConfigAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IDeleteAutoDebitConfigResponse;
      })
      .addCase(deleteAutoDebitConfigAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IDeleteAutoDebitConfigResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetDeleteAutoDebitConfig } = deleteAutoDebitConfigSlice.actions;

export default deleteAutoDebitConfigSlice.reducer;
